import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input,
  OnChanges, Output, SimpleChanges,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {A11yModule} from '@angular/cdk/a11y';
import {MatIcon} from '@angular/material/icon';
import {finalize} from 'rxjs';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {BusinessUnitDTO, UpdateBusinessUnitDTO} from '../../../shared/model/bench-entry.model';
import {MessageService} from '../../../shared/components/message.service';

interface Row extends BusinessUnitDTO {
  isDraft?: boolean;
}

const DEFAULT_COLORS = {color: '#1565C0', bg: '#E8F0FE', ring: '#BBD5F5'};

@Component({
  selector: 'app-bu-catalog-modal',
  templateUrl: './bu-catalog-modal.component.html',
  styleUrl: './bu-catalog-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, MatIcon, A11yModule],
})
export class BuCatalogModalComponent implements OnChanges {
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<BusinessUnitDTO[]>();
  @Output() entriesInvalidated = new EventEmitter<void>();

  catalog: Row[] = [];
  pendingToggle: {row: Row; target: string} | null = null;
  isLoading = false;
  private readonly savingIds = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reload();
    }
  }

  private reload(): void {
    this.isLoading = true;
    this.pendingToggle = null;
    this.benchService.getBusinessUnits()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: catalog => {
          this.catalog = catalog.map(b => ({...b}));
          this.saved.emit(catalog);
        },
        error: () => this.messageService.error('benchBu.loadError'),
      });
  }

  get totalCount(): number {
    return this.catalog.filter(b => !b.isDraft).length;
  }

  get activeCount(): number {
    return this.catalog.filter(b => !b.isDraft && b.active).length;
  }

  isSaving(row: Row): boolean {
    return !!row.id && this.savingIds.has(row.id);
  }

  addBu(): void {
    this.catalog = [
      ...this.catalog,
      {
        id: '', key: '', name: '', ...DEFAULT_COLORS,
        active: true, usageCount: 0, isDraft: true,
      },
    ];
  }

  onKeyInput(row: Row, raw: string): void {
    row.key = (raw ?? '').toUpperCase().slice(0, 4);
  }

  persistDraft(row: Row): void {
    if (!row.isDraft || !row.name.trim()) return;
    this.benchService.createBusinessUnit({
      key: row.key || undefined,
      name: row.name.trim(),
      color: row.color,
      bg: row.bg,
      ring: row.ring,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: created => {
          this.catalog = this.catalog.map(r => r === row ? {...created} : r);
          this.saved.emit(this.snapshotDtos());
          this.cdr.markForCheck();
        },
        error: (err: unknown) => this.handleError(err, 'create'),
      });
  }

  persistUpdate(row: Row): void {
    if (row.isDraft) {
      this.persistDraft(row);
      return;
    }
    if (!row.name.trim()) return;
    this.savingIds.add(row.id);
    const body: UpdateBusinessUnitDTO = {
      key: row.key || undefined,
      name: row.name.trim(),
      color: row.color,
      bg: row.bg,
      ring: row.ring,
    };
    this.benchService.updateBusinessUnit(row.id, body)
      .pipe(
        finalize(() => {
          this.savingIds.delete(row.id);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: updated => {
          this.catalog = this.catalog.map(r => r.id === updated.id ? {...updated} : r);
          this.saved.emit(this.snapshotDtos());
        },
        error: (err: unknown) => this.handleError(err, 'update'),
      });
  }

  removeBu(row: Row): void {
    if (row.isDraft) {
      this.catalog = this.catalog.filter(r => r !== row);
      return;
    }
    this.benchService.deleteBusinessUnit(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.catalog = this.catalog.filter(r => r.id !== row.id);
          this.saved.emit(this.snapshotDtos());
          this.cdr.markForCheck();
        },
        error: (err: unknown) => this.handleError(err, 'delete'),
      });
  }

  onToggleClick(row: Row): void {
    if (row.isDraft || !row.id) return;
    if (row.active && row.usageCount > 0) {
      const first = this.catalog.find(b => b.id !== row.id && b.active && !b.isDraft);
      this.pendingToggle = {row, target: first?.id ?? ''};
      return;
    }
    this.applyActivation(row, !row.active);
  }

  get pendingUsageCount(): number {
    return this.pendingToggle?.row.usageCount ?? 0;
  }

  migrationTargets(): Row[] {
    if (!this.pendingToggle) return [];
    return this.catalog.filter(b => !b.isDraft && b.active && b.id !== this.pendingToggle!.row.id);
  }

  cancelPendingToggle(): void {
    this.pendingToggle = null;
  }

  confirmPendingToggle(): void {
    if (!this.pendingToggle || !this.pendingToggle.target) return;
    const {row, target} = this.pendingToggle;
    this.pendingToggle = null;
    this.applyActivation(row, false, target);
  }

  private applyActivation(row: Row, active: boolean, targetBusinessUnitId?: string): void {
    this.savingIds.add(row.id);
    const rewritesOffers = !active && !!targetBusinessUnitId;
    this.benchService.setBusinessUnitActivation(row.id, {active, targetBusinessUnitId})
      .pipe(
        finalize(() => {
          this.savingIds.delete(row.id);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          if (rewritesOffers) this.entriesInvalidated.emit();
          this.reload();
        },
        error: (err: unknown) => this.handleError(err, 'activate'),
      });
  }

  private snapshotDtos(): BusinessUnitDTO[] {
    return this.catalog
      .filter(r => !r.isDraft)
      .map(({isDraft: _isDraft, ...dto}) => dto);
  }

  close(): void {
    this.visible = false;
    this.pendingToggle = null;
    this.visibleChange.emit(false);
  }

  private handleError(err: unknown, op: 'create' | 'update' | 'delete' | 'activate'): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const type = err instanceof HttpErrorResponse ? err.error?.type : '';
    let key = 'benchBu.saveError';
    if (op === 'delete' && status === 409) key = 'benchBu.error.inUse';
    else if (typeof type === 'string' && type.endsWith('/business-unit-key-exists')) key = 'benchBu.error.keyExists';
    else if (typeof type === 'string' && type.endsWith('/business-unit-name-exists')) key = 'benchBu.error.nameExists';
    this.messageService.error(key);
    if (op !== 'create') this.reload();
  }
}
